"use client";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, Typography, Container } from "@mui/material";
import { useTranslation } from "react-i18next";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { IMAGES } from "@/constants/images";

const AGENTS = [
  {
    key: "zayn",
    imgKey: "ZAYN_IMG",
    gradientFrom: "#9810FA",
    gradientTo: "#7F22FE",
    resultBgImg: IMAGES.ZAYN_RESULT_BG,
    resultBorder: "#7C3AED",
  },
  {
    key: "atlas",
    imgKey: "ATLAS_IMG",
    gradientFrom: "#54B3D6",
    gradientTo: "#54B3D6",
    resultBgImg: IMAGES.ATLAS_RESULT_BG,
    resultBorder: "#059669",
  },
  {
    key: "sage",
    imgKey: "SAGE_IMG",
    gradientFrom: "#F57C75",
    gradientTo: "#F57C75",
    resultBgImg: IMAGES.SAGE_RESULT_BG,
    resultBorder: "#F97316",
  },
  {
    key: "scout",
    imgKey: "SCOUT_IMG",
    gradientFrom: "#E566CB",
    gradientTo: "#E566CB",
    resultBgImg: IMAGES.SCOUT_RESULT_BG,
    resultBorder: "#EC4899",
  },
  {
    key: "compass",
    imgKey: "COMPASS_IMG",
    gradientFrom: "#628EDD",
    gradientTo: "#628EDD",
    resultBgImg: IMAGES.COMPASS_RESULT_BG,
    resultBorder: "#3B82F6",
  },
  {
    key: "omni",
    imgKey: "OMNI_IMG",
    gradientFrom: "#FE895F",
    gradientTo: "#FE895F",
    resultBgImg: IMAGES.OMNI_RESULT_BG,
    resultBorder: "#F59E0B",
  },
];

const AUTO_CYCLE_DURATION = 15000;

const AIAgentsSectionAds = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  const [activeIndex, setActiveIndex] = useState(0);
  const [showCta, setShowCta] = useState(true);
  const [isVisible, setIsVisible] = useState(false);

  const [loaderProgress, setLoaderProgress] = useState(0);
  const progressRef = useRef(0);
  const agentRefs = useRef([]);
  const activeAgent = AGENTS[activeIndex];
  const cycleTimerRef = useRef(null);
  const rafRef = useRef(null);
  const startTimeRef = useRef(null);
  const pausedProgressRef = useRef(null);
  const isPausedRef = useRef(false);
  const sectionRef = useRef(null);
  const hasStartedRef = useRef(false);
  const scrollContainerRef = useRef(null);
  const UI_BOX_SHADOW =
    "0px 15px 35px rgba(0,0,0,0.04), 0px 5px 15px rgba(0,0,0,0.02)";

  const bulletKeys = [0, 1, 2, 3, 4].map(
    (i) => `aiAgentsSectionAds.agents.${activeAgent.key}.bullets.${i}`,
  );

  const startLoader = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (cycleTimerRef.current) clearTimeout(cycleTimerRef.current);

    isPausedRef.current = false;
    pausedProgressRef.current = null;
    progressRef.current = 0;
    setLoaderProgress(0);
    startTimeRef.current = null;

    const animate = (timestamp) => {
      if (isPausedRef.current) return;
      if (!startTimeRef.current) startTimeRef.current = timestamp;
      const elapsed = timestamp - startTimeRef.current;
      const progress = Math.min((elapsed / AUTO_CYCLE_DURATION) * 100, 100);
      progressRef.current = progress;
      setLoaderProgress(progress);

      if (progress < 100) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        setActiveIndex((prev) => (prev + 1) % AGENTS.length);
      }
    };

    rafRef.current = requestAnimationFrame(animate);
  }, []);

  const resumeLoader = useCallback(() => {
    if (!isPausedRef.current) return;
    isPausedRef.current = false;

    const resumedFrom = pausedProgressRef.current ?? 0;
    const alreadyElapsed = (resumedFrom / 100) * AUTO_CYCLE_DURATION;

    const animate = (timestamp) => {
      if (isPausedRef.current) return;
      if (!startTimeRef.current) {
        startTimeRef.current = timestamp - alreadyElapsed;
      }
      const elapsed = timestamp - startTimeRef.current;
      const progress = Math.min((elapsed / AUTO_CYCLE_DURATION) * 100, 100);
      setLoaderProgress(progress);

      if (progress < 100) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        setActiveIndex((prev) => (prev + 1) % AGENTS.length);
      }
    };

    startTimeRef.current = null;
    rafRef.current = requestAnimationFrame(animate);
  }, []);

  const pauseLoader = useCallback(() => {
    if (isPausedRef.current) return;
    isPausedRef.current = true;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    setLoaderProgress((prev) => {
      pausedProgressRef.current = prev;
      return prev;
    });
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    startLoader();
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (cycleTimerRef.current) clearTimeout(cycleTimerRef.current);
    };
  }, [activeIndex, startLoader, isVisible]);
  useEffect(() => {
    const container = scrollContainerRef.current;
    const activeEl = agentRefs.current[activeIndex];
    if (!container || !activeEl) return;

    const isHorizontallyScrollable =
      container.scrollWidth > container.clientWidth;
    if (!isHorizontallyScrollable) return;

    const containerRect = container.getBoundingClientRect();
    const elRect = activeEl.getBoundingClientRect();
    const offset =
      elRect.left -
      containerRect.left -
      containerRect.width / 2 +
      elRect.width / 2;

    container.scrollBy({ left: offset, behavior: "smooth" });
  }, [activeIndex]);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasStartedRef.current) {
            hasStartedRef.current = true;
            setIsVisible(true);
            setActiveIndex(0);
            progressRef.current = 0;
            setLoaderProgress(0);
            isPausedRef.current = false;
          }
          if (!entry.isIntersecting) {
            hasStartedRef.current = false;
            setIsVisible(false);
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            isPausedRef.current = true;
          }
        });
      },
      { threshold: 0.2 },
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleAgentClick = (i) => {
    if (i !== activeIndex) {
      progressRef.current = 0;
      setLoaderProgress(0);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      isPausedRef.current = false;
      setActiveIndex(i);
    }
  };

  return (
    <Box
      ref={sectionRef}
      sx={{
        backgroundColor: "#fff",
        py: { xs: 4, sm: 6, md: 10 },
        direction: isArabic ? "rtl" : "ltr",
        overflowX: "hidden",
        width: "100%",
      }}
    >
      <Container
        maxWidth="lg"
        sx={{ px: { xs: 2, sm: 3, md: 4 }, overflowX: "hidden" }}
      >
        {/* ── HEADER ── */}
        <Box sx={{ textAlign: "center", mb: { xs: 4, sm: 5, md: 8 } }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.8,
              border: "1px solid #eadffd",
              backgroundColor: "#eadffd",
              borderRadius: "50px",
              px: { xs: 1.8, sm: 2.5 },
              py: { xs: 0.7, sm: 1 },
              mb: { xs: 2, sm: 3 },
            }}
          >
            <Box
              component="img"
              src={IMAGES.BADGE_PURPLE_IMG}
              alt="badge icon"
              sx={{ width: { xs: 13, sm: 16 }, height: { xs: 13, sm: 16 } }}
            />
            <Typography
              sx={{
                fontFamily: "var(--font-outfit)",
                fontSize: { xs: "0.68rem", sm: "0.75rem", md: "0.8rem" },
                fontWeight: 500,
                color: "#8200DB",
                letterSpacing: "0.05em",
                textTransform: "uppercase",
              }}
            >
              {t("aiAgentsSectionAds.badge")}
            </Typography>
          </Box>

          <Typography
            component="h2"
            sx={{
              fontFamily: "var(--font-plusjakarta)",
              fontWeight: 800,
              fontSize: { xs: "1.6rem", sm: "2.2rem", md: "3.5rem" },
              lineHeight: { xs: 1.25, sm: 1.2, md: 1.15 },
              color: "#101828",
              mb: { xs: 1.5, sm: 2 },
              maxWidth: 890,
              mx: "auto",
              letterSpacing: "-0.02em",
            }}
          >
            {t("aiAgentsSectionAds.heading")}
          </Typography>

          <Typography
            sx={{
              fontFamily: "var(--font-outfit)",
              fontSize: { xs: "0.85rem", sm: "1rem", md: "1.3rem" },
              color: "#4A5565",
              lineHeight: 1.6,
              maxWidth: 800,
              mx: "auto",
            }}
          >
            {t("aiAgentsSectionAds.subtext")}
          </Typography>
        </Box>

        {/* ── MAIN LAYOUT ── */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr", md: "350px 1fr" },
            gap: { xs: 2.5, sm: 3, md: 4 },
            alignItems: "stretch",
            width: "100%",
            minWidth: 0,
          }}
        >
          {/* ── LEFT ── */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: { xs: 1, sm: 1.5 },
              minWidth: 0,
            }}
          >
            <Box
              ref={scrollContainerRef}
              sx={{
                display: { xs: "flex", sm: "flex", md: "flex" },
                flexDirection: { xs: "row", sm: "row", md: "column" },
                gap: { xs: 1, sm: 1.2, md: 1.5 },
                overflowX: { xs: "auto", sm: "auto", md: "unset" },
                pb: { xs: 1, sm: 1, md: 0 },
                mx: { xs: -2, sm: -3, md: 0 },
                px: { xs: 2, sm: 3, md: 0 },
                "&::-webkit-scrollbar": {
                  display: { xs: "none", sm: "none", md: "block" },
                },
                scrollbarWidth: { xs: "none", sm: "none", md: "auto" },
              }}
            >
              {AGENTS.map((agent, i) => {
                const isActive = i === activeIndex;
                return (
                  <Box
                    key={agent.key}
                    ref={(el) => (agentRefs.current[i] = el)}
                    onClick={() => handleAgentClick(i)}
                    onMouseEnter={isActive ? pauseLoader : undefined}
                    onMouseLeave={isActive ? resumeLoader : undefined}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: { xs: 1, sm: 1.5, md: 2 },
                      px: { xs: 1.2, sm: 2, md: 2.5 },
                      py: { xs: 1.2, sm: 1.5, md: 2 },
                      borderRadius: { xs: "14px", sm: "16px", md: "18px" },
                      cursor: "pointer",
                      border: isActive
                        ? `1.5px solid ${agent.gradientTo}33`
                        : "1.5px solid #F1F5F9",
                      backgroundColor: "#fff",
                      boxShadow: isActive
                        ? `0px 8px 30px rgba(0,0,0,0.10), 0px 2px 8px rgba(0,0,0,0.06)`
                        : "none",
                      transition: "all 0.25s ease",
                      flexShrink: 0,
                      width: { xs: "auto", sm: "auto", md: "100%" },
                      boxSizing: "border-box",
                      position: "relative",
                      overflow: "hidden",
                      "&:hover": {
                        boxShadow: `0px 8px 30px rgba(0,0,0,0.08), 0px 2px 8px rgba(0,0,0,0.04)`,
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={IMAGES[agent.imgKey]}
                      alt={t(`aiAgentsSectionAds.agents.${agent.key}.name`)}
                      sx={{
                        width: { xs: 36, sm: 44, md: 60 },
                        height: { xs: 36, sm: 44, md: 60 },
                        borderRadius: "50%",
                        objectFit: "cover",
                        flexShrink: 0,
                      }}
                    />

                    <Box sx={{ flex: 1 }}>
                      <Typography
                        sx={{
                          fontFamily: "var(--font-plusjakarta)",
                          fontWeight: 700,
                          fontSize: {
                            xs: "0.82rem",
                            sm: "0.95rem",
                            md: "1.16rem",
                          },
                          color: isActive ? agent.gradientTo : "#101828",
                          lineHeight: 1.2,
                          transition: "color 0.2s ease",
                          mb: { xs: 1, sm: 0.5, md: 1 },
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t(`aiAgentsSectionAds.agents.${agent.key}.name`)}
                      </Typography>
                      <Typography
                        sx={{
                          display: { xs: "none", sm: "none", md: "block" },
                          fontFamily: "var(--font-outfit)",
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          color: "#4A5565",
                          letterSpacing: "0.07em",
                          textTransform: "uppercase",
                          mt: 0.3,
                        }}
                      >
                        {t(`aiAgentsSectionAds.agents.${agent.key}.role`)}
                      </Typography>
                    </Box>

                    {isActive && (
                      <ArrowForwardIcon
                        sx={{
                          display: { xs: "none", sm: "none", md: "block" },
                          color: agent.gradientTo,
                          fontSize: "1.3rem",
                          flexShrink: 0,
                          transform: isArabic ? "rotate(180deg)" : "none",
                        }}
                      />
                    )}

                    {isActive && (
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: 0,
                          left: 0,
                          height: "3px",
                          width: `${loaderProgress}%`,
                          backgroundColor: agent.gradientTo,
                          borderRadius: "0 2px 2px 0",
                        }}
                      />
                    )}
                  </Box>
                );
              })}
            </Box>

            {/* CTA Card — hide on xs */}
            {showCta && (
              <Box
                sx={{
                  display: { xs: "none", sm: "block", md: "block" },
                  backgroundImage: `url(${IMAGES.CTA_BG_ADS})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  borderRadius: { xs: "16px", sm: "18px", md: "20px" },
                  p: { xs: 2.5, sm: 3 },
                  position: "relative",
                  overflow: "hidden",
                  mt: 2,
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    right: 20,
                    bottom: -20,
                    width: 60,
                    height: 60,
                    borderRadius: "50%",
                    backgroundColor: "rgba(255,255,255,0.05)",
                    pointerEvents: "none",
                  }}
                />

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 0.8,
                  }}
                >
                  <Box
                    component="img"
                    src={IMAGES.ICON_WITH_BG_CTA}
                    alt="icon"
                    sx={{ width: 40, height: 40, flexShrink: 0 }}
                  />
                  <Typography
                    sx={{
                      fontFamily: "var(--font-plusjakarta)",
                      fontWeight: 700,
                      fontSize: { xs: "0.95rem", sm: "1rem", md: "1.1rem" },
                      color: "#fff",
                    }}
                  >
                    {t("aiAgentsSectionAds.ctaCard.title")}
                  </Typography>
                </Box>

                <Typography
                  sx={{
                    fontFamily: "var(--font-outfit)",
                    fontSize: { xs: "0.82rem", sm: "0.85rem", md: "0.9rem" },
                    color: "#EDE9FE",
                    lineHeight: 1.5,
                    mb: 2.5,
                    mt: 2,
                  }}
                >
                  {t("aiAgentsSectionAds.ctaCard.desc")}
                </Typography>

                <Box
                  id="btn-four"
                  component="button"
                  // onClick={() => {
                  //   const el = document.getElementById("book-demo-section");
                  //   if (el) el.scrollIntoView({ behavior: "smooth" });
                  // }}
                  onClick={() => {
                    window.dispatchEvent(new Event("open-book-demo-modal"));
                  }}
                  sx={{
                    width: "100%",
                    backgroundColor: "#fff",
                    border: "none",
                    borderRadius: "10px",
                    py: { xs: 1.4, sm: 1.7 },
                    px: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    cursor: "pointer",
                    fontFamily: "var(--font-outfit)",
                    fontSize: "1rem",
                    fontWeight: 600,
                    color: "#FFB200",
                    transition: "all 0.3s ease-in-out",
                    "&:hover": { opacity: 0.9, transform: "scale(1.05)" },
                  }}
                >
                  {t("aiAgentsSectionAds.ctaCard.cta")}
                  <ArrowForwardIcon
                    sx={{
                      fontSize: "1.3rem",
                      color: "#FFB200",
                      transform: isArabic ? "rotate(180deg)" : "none",
                      mt: 0.3,
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>

          {/* ── RIGHT — Agent Detail Panel ── */}
          <Box
            sx={{
              backgroundColor: "#fff",
              border: "1px solid #F1F5F9",
              borderRadius: { xs: "18px", sm: "20px", md: "24px" },
              p: { xs: 2, sm: 2.5, md: 4 },
              boxShadow: UI_BOX_SHADOW,
              width: "100%",
              minWidth: 0,
              boxSizing: "border-box",
              overflowX: "hidden",
              wordBreak: "break-word",
              overflowY: "auto",
            }}
          >
            {/* Agent Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1.5, sm: 2 },
                mb: { xs: 2, sm: 2.5, md: 3 },
              }}
            >
              <Box
                component="img"
                src={IMAGES[activeAgent.imgKey]}
                alt={t(`aiAgentsSectionAds.agents.${activeAgent.key}.name`)}
                sx={{
                  width: { xs: 40, sm: 46, md: 52 },
                  height: { xs: 40, sm: 46, md: 52 },
                  borderRadius: "50%",
                  objectFit: "cover",
                  flexShrink: 0,
                }}
              />
              <Box>
                <Typography
                  sx={{
                    fontFamily: "var(--font-plusjakarta)",
                    fontWeight: 800,
                    fontSize: { xs: "1.2rem", sm: "1.4rem", md: "1.6rem" },
                    color: "#101828",
                    background: `linear-gradient(90deg, ${activeAgent.gradientFrom} 0%, ${activeAgent.gradientTo} 100%)`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  {t(`aiAgentsSectionAds.agents.${activeAgent.key}.name`)}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "var(--font-outfit)",
                    fontSize: { xs: "0.65rem", sm: "0.72rem", md: "0.8rem" },
                    fontWeight: 600,
                    color: activeAgent.gradientTo,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    mt: 0.5,
                  }}
                >
                  {t(`aiAgentsSectionAds.agents.${activeAgent.key}.role`)}
                </Typography>
              </Box>
            </Box>

            {/* ── PROBLEM BANNER ── */}
            <Box
              sx={{
                backgroundColor: "#FEF2F2",
                border: "1px solid #FED7D7",
                borderRadius: { xs: "10px", sm: "11px", md: "12px" },
                px: { xs: 2, sm: 2.5 },
                py: { xs: 1.5, sm: 2 },
                mb: { xs: 2, sm: 2.5, md: 3 },
                width: "100%",
                boxSizing: "border-box",
                overflowX: "hidden",
              }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.6,
                  mb: 1.2,
                  backgroundColor: "#FFE2E2",
                  borderRadius: "50px",
                  px: 1.5,
                  py: 0.5,
                }}
              >
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    backgroundColor: "#FB2C36",
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: "var(--font-outfit)",
                    fontSize: { xs: "0.62rem", sm: "0.65rem", md: "0.7rem" },
                    fontWeight: 700,
                    color: "#C10007",
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                  }}
                >
                  {t("aiAgentsSectionAds.problemLabel")}
                </Typography>
              </Box>

              <Typography
                sx={{
                  fontFamily: "var(--font-plusjakarta)",
                  fontSize: { xs: "0.85rem", sm: "0.9rem", md: "0.92rem" },
                  fontWeight: 700,
                  color: "#1A202C",
                  lineHeight: 1.4,
                  mb: 0.5,
                }}
              >
                {t(
                  `aiAgentsSectionAds.agents.${activeAgent.key}.problemHeading`,
                )}
              </Typography>

              <Typography
                sx={{
                  fontFamily: "var(--font-outfit)",
                  fontSize: { xs: "0.8rem", sm: "0.82rem", md: "0.85rem" },
                  color: "#4A5568",
                  lineHeight: 1.55,
                  fontWeight: 400,
                }}
              >
                {t(`aiAgentsSectionAds.agents.${activeAgent.key}.problem`)}
              </Typography>
            </Box>

            {/* Main Heading */}
            <Typography
              component="h3"
              sx={{
                fontFamily: "var(--font-plusjakarta)",
                fontWeight: 800,
                fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.65rem" },
                color: "#101828",
                lineHeight: 1.4,
                mb: { xs: 1.5, sm: 2 },
                letterSpacing: "-0.02em",
              }}
            >
              {t(`aiAgentsSectionAds.agents.${activeAgent.key}.heading`)}
            </Typography>

            {/* Description */}
            <Typography
              sx={{
                fontFamily: "var(--font-outfit)",
                fontSize: { xs: "0.85rem", sm: "0.9rem", md: "0.98rem" },
                color: "#364153",
                lineHeight: 1.7,
                mb: { xs: 2, sm: 2.5, md: 3 },
                fontWeight: 400,
              }}
            >
              {t(`aiAgentsSectionAds.agents.${activeAgent.key}.desc`)}
            </Typography>

            {/* Bullet Points */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: { xs: 0.8, sm: 1 },
                mb: { xs: 2, sm: 2.5, md: 3 },
              }}
            >
              {bulletKeys.map((bKey, idx) => {
                const text = t(bKey, { defaultValue: "" });
                if (!text) return null;
                return (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: { xs: 0.8, sm: 1 },
                    }}
                  >
                    <Box
                      component="img"
                      src="https://getgeek-website-assets.b-cdn.net/Ads_correct_icon.webp"
                      alt="check"
                      sx={{
                        width: { xs: 20, sm: 22, md: 25 },
                        height: { xs: 20, sm: 22, md: 25 },
                        mt: 0.2,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{
                        fontFamily: "var(--font-outfit)",
                        fontSize: {
                          xs: "0.82rem",
                          sm: "0.9rem",
                          md: "0.98rem",
                        },
                        color: "#364153",
                        lineHeight: 1.5,
                        fontWeight: 400,
                      }}
                    >
                      {text}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            {/* Result Card */}
            <Box
              sx={{
                background: activeAgent.resultBgImg
                  ? `url(${activeAgent.resultBgImg})`
                  : activeAgent.resultBg,
                backgroundSize: "cover",
                backgroundPosition: "center",
                borderRadius: { xs: "12px", sm: "14px", md: "16px" },
                p: { xs: 2, sm: 2.5, md: 3 },
                position: "relative",
                overflow: "hidden",
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.8,
                  mb: 1,
                }}
              >
                <Box
                  component="img"
                  src="https://getgeek-website-assets.b-cdn.net/WhiteAwesomeIcon.webp"
                  alt="awesome"
                  sx={{
                    width: { xs: 13, sm: 14, md: 16 },
                    height: { xs: 13, sm: 14, md: 16 },
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: "var(--font-plusjakarta)",
                    fontSize: { xs: "0.85rem", sm: "0.92rem", md: "1rem" },
                    fontWeight: 700,
                    color: "#fff",
                    letterSpacing: "0.1em",
                  }}
                >
                  {t("aiAgentsSectionAds.resultLabel")}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontFamily: "var(--font-outfit)",
                  fontSize: { xs: "0.82rem", sm: "0.88rem", md: "0.95rem" },
                  color: "#fff",
                  lineHeight: 1.6,
                  fontWeight: 400,
                }}
              >
                {t(`aiAgentsSectionAds.agents.${activeAgent.key}.result`)}
              </Typography>

              <Box
                sx={{
                  position: "absolute",
                  right: -20,
                  top: -20,
                  width: 100,
                  height: 100,
                  borderRadius: "50%",
                  backgroundColor: "rgba(255,255,255,0.06)",
                  pointerEvents: "none",
                }}
              />
            </Box>

            {/* CTA Card — xs only */}
            {showCta && (
              <Box
                sx={{
                  display: { xs: "block", sm: "none", md: "none" },
                  backgroundImage: `url(${IMAGES.CTA_BG_ADS})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  borderRadius: "16px",
                  p: 2.5,
                  position: "relative",
                  overflow: "hidden",
                  mt: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 1.5,
                  }}
                >
                  <Box
                    component="img"
                    src={IMAGES.ICON_WITH_BG_CTA}
                    alt="icon"
                    sx={{ width: 36, height: 36, flexShrink: 0 }}
                  />
                  <Typography
                    sx={{
                      fontFamily: "var(--font-plusjakarta)",
                      fontWeight: 700,
                      fontSize: "0.95rem",
                      color: "#fff",
                      lineHeight: 1.2,
                    }}
                  >
                    {t("aiAgentsSectionAds.ctaCard.title")}
                  </Typography>
                </Box>

                <Typography
                  sx={{
                    fontFamily: "var(--font-outfit)",
                    fontSize: "0.82rem",
                    color: "#EDE9FE",
                    lineHeight: 1.5,
                    mb: 2,
                  }}
                >
                  {t("aiAgentsSectionAds.ctaCard.desc")}
                </Typography>

                <Box
                  component="button"
                  sx={{
                    width: "100%",
                    backgroundColor: "#fff",
                    border: "none",
                    borderRadius: "10px",
                    py: 1.4,
                    px: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    cursor: "pointer",
                    fontFamily: "var(--font-outfit)",
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    color: "#FFB200",
                    transition: "all 0.3s ease-in-out",
                    "&:hover": { opacity: 0.9, transform: "scale(1.05)" },
                  }}
                >
                  {t("aiAgentsSectionAds.ctaCard.cta")}
                  <ArrowForwardIcon
                    sx={{
                      fontSize: "0.95rem",
                      color: "#FFB200",
                      transform: isArabic ? "rotate(180deg)" : "none",
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default AIAgentsSectionAds;
